// AntD ConfigProvider theme — sources values from `@ds/tokens/palette` so
// AntD widgets across the entire app render with the editorial palette.
// Pass to <ConfigProvider theme={antdTheme}> at the app root.
//
// Globally reskinning to editorial: AntD widgets (Buttons, Inputs, Modals,
// Tables, Tabs, Tags, Forms, etc.) pick up these tokens automatically.
// Legacy app code that hardcodes hex from `@ds/tokens/tokens` (the legacy
// palette) keeps its current look until each page migrates to @ds primitives.

import type { ThemeConfig } from "antd";

import {
  CONTROL_HEIGHT,
  FONT_FAMILY,
  FONT_SIZE,
  LINE_HEIGHT,
  RADIUS,
  SHADOW,
  SPACE,
  SIDEBAR,
  SURFACE,
  TEXT,
  TONE,
} from "@ds/tokens/palette";

export const antdTheme: ThemeConfig = {
  token: {
    // ── Brand + semantics ──────────────────────────────────────────────
    colorPrimary: TONE.blue.base,
    colorInfo:    TONE.blue.base,
    colorSuccess: TONE.green.base,
    colorWarning: TONE.amber.base,
    colorError:   TONE.red.base,

    // ── Text (solid hex; AntD's defaults use rgba on white) ────────────
    colorText:           TEXT.primary,
    colorTextSecondary:  TEXT.secondary,
    colorTextTertiary:   TEXT.muted,
    colorTextQuaternary: TEXT.faint,

    // ── Surfaces ───────────────────────────────────────────────────────
    colorBgContainer: SURFACE.surface,
    colorBgElevated:  SURFACE.surface,
    colorBgLayout:    SURFACE.bg,
    colorBgBase:      SURFACE.surface,

    // ── Borders ────────────────────────────────────────────────────────
    colorBorder:          SURFACE.border,
    colorBorderSecondary: SURFACE.borderSoft,
    colorSplit:           SURFACE.borderSoft,

    // ── Radius ─────────────────────────────────────────────────────────
    borderRadius:   RADIUS.md,
    borderRadiusLG: RADIUS.lg,
    borderRadiusSM: RADIUS.xs,
    borderRadiusXS: 2,                         // off-scale; AntD-internal only

    // ── Controls ───────────────────────────────────────────────────────
    controlHeight:       CONTROL_HEIGHT.md,
    controlHeightSM:     CONTROL_HEIGHT.sm,
    controlHeightLG:     CONTROL_HEIGHT.lg,
    controlOutlineWidth: 2,

    // ── Typography ─────────────────────────────────────────────────────
    fontFamily:         FONT_FAMILY.sans,
    fontFamilyCode:     FONT_FAMILY.mono,
    fontSize:           FONT_SIZE.normal,
    fontSizeSM:         FONT_SIZE.small,
    fontSizeLG:         FONT_SIZE.subheading,
    fontSizeXL:         FONT_SIZE.large,
    fontSizeHeading1:   FONT_SIZE.h1,
    fontSizeHeading2:   FONT_SIZE.h2,
    fontSizeHeading3:   FONT_SIZE.large,
    fontSizeHeading4:   FONT_SIZE.subheading,
    fontSizeHeading5:   FONT_SIZE.normal,
    lineHeight:         LINE_HEIGHT.normal,
    lineHeightHeading1: LINE_HEIGHT.tight,

    // ── Spacing ────────────────────────────────────────────────────────
    padding:   SPACE.lg,
    paddingXS: SPACE.sm,
    paddingSM: SPACE.md,
    paddingLG: SPACE.xl,
    paddingXL: SPACE.xxl,
    margin:    SPACE.lg,
    marginXS:  SPACE.sm,
    marginSM:  SPACE.md,
    marginLG:  SPACE.xl,
    marginXL:  SPACE.xxl,

    // ── Shadow + motion ────────────────────────────────────────────────
    boxShadow:          SHADOW.md,
    boxShadowSecondary: SHADOW.sm,
    boxShadowTertiary:  SHADOW.xs,

    motionDurationFast: "0.12s",
    motionDurationMid:  "0.2s",
    // Operator tool: popups/menus/drawers appear instantly — also removes the rc-motion inert-popup window that swallows the first click.
    motion: false,

    wireframe: false,
  },

  components: {
    Button: {
      fontWeight:      500,
      primaryShadow:   SHADOW.xs,
      defaultShadow:   SHADOW.xs,
      dangerShadow:    SHADOW.xs,
      controlHeight:   CONTROL_HEIGHT.md,
      controlHeightSM: CONTROL_HEIGHT.sm,
      controlHeightLG: CONTROL_HEIGHT.lg,
      paddingInline:   SPACE.lg,
      paddingInlineSM: SPACE.md,
      paddingInlineLG: 20,                      // off-scale; AntD-internal
      borderRadius:    RADIUS.lg,
    },
    Input: {
      controlHeight:     CONTROL_HEIGHT.md,
      controlHeightSM:   CONTROL_HEIGHT.sm,
      controlHeightLG:   CONTROL_HEIGHT.lg,
      paddingBlock:      5,                      // off-scale
      paddingInline:     SPACE.md,
      borderRadius:      RADIUS.lg,
      activeShadow:      SHADOW.focusBlue,
      errorActiveShadow: SHADOW.focusRed,
      hoverBorderColor:  TONE.blue.base,
      activeBorderColor: TONE.blue.base,
    },
    Select: {
      controlHeight:    CONTROL_HEIGHT.md,
      controlHeightSM:  CONTROL_HEIGHT.sm,
      controlHeightLG:  CONTROL_HEIGHT.lg,
      borderRadius:     RADIUS.lg,
      optionSelectedBg: TONE.blue.softBg,
      optionActiveBg:   SURFACE.surfaceHover,
    },
    DatePicker: {
      controlHeight:     CONTROL_HEIGHT.md,
      borderRadius:      RADIUS.lg,
      activeShadow:      SHADOW.focusBlue,
      hoverBorderColor:  TONE.blue.base,
      activeBorderColor: TONE.blue.base,
    },
    Card: {
      headerBg:          "transparent",
      headerFontSize:    FONT_SIZE.normal,
      headerFontSizeSM:  FONT_SIZE.normal,
      headerHeight:      48,                      // off-scale
      headerHeightSM:    CONTROL_HEIGHT.lg,
      borderRadiusLG:    RADIUS.lg,
      paddingLG:         SPACE.lg,
      boxShadowTertiary: "none",
    },
    Tag: {
      borderRadiusSM: RADIUS.xs,
      defaultBg:      SURFACE.surfaceSoft,
      defaultColor:   TEXT.secondary,
    },
    Alert: {
      borderRadiusLG:          RADIUS.sm,
      withDescriptionPadding:  `${SPACE.lg}px ${SPACE.lg}px`,
      withDescriptionIconSize: 20,                // off-scale
      defaultPadding:          `${SPACE.sm}px ${SPACE.md}px`,
    },
    Table: {
      // Table chrome is intentionally limited to AntD component tokens.
      headerBg:            SURFACE.surface,
      headerColor:         TEXT.muted,
      headerSplitColor:    SURFACE.border,
      rowHoverBg:          SURFACE.surfaceHover,
      borderColor:         SURFACE.borderSoft,
      cellPaddingBlock:    SPACE.md,
      cellPaddingInline:   SPACE.md,
      cellPaddingBlockMD:  SPACE.sm,
      cellPaddingInlineMD: SPACE.md,
      cellPaddingBlockSM:  SPACE.sm,
      cellPaddingInlineSM: SPACE.sm,
      fontSize:            FONT_SIZE.normal,
      borderRadius:        RADIUS.sm,
    },
    Tabs: {
      horizontalItemPadding: `${SPACE.sm}px ${SPACE.md}px`,
      horizontalItemGutter:  SPACE.lg,
      horizontalMargin:      `0 0 ${SPACE.lg}px 0`,
      titleFontSize:         FONT_SIZE.normal,
      inkBarColor:           TONE.blue.base,
      itemColor:             TEXT.secondary,
      itemSelectedColor:     TONE.blue.base,
      itemHoverColor:        TEXT.primary,
    },
    Modal: {
      borderRadiusLG: RADIUS.xl,
      headerBg:       SURFACE.surface,
      titleFontSize:  FONT_SIZE.subheading,
      padding:        SPACE.xl,
      paddingMD:      SPACE.lg,
    },
    Drawer: {
      borderRadiusLG: 0,
      padding:        SPACE.lg,
      paddingLG:      SPACE.xl,
    },
    Tooltip: {
      borderRadius:     RADIUS.sm,
      colorBgSpotlight: "rgba(0, 0, 0, 0.85)",   // off-scale
      fontSize:         FONT_SIZE.small,
      paddingXS:        6,                        // off-scale
      paddingSM:        SPACE.sm,
    },
    Popover: {
      borderRadiusLG:     RADIUS.sm,
      padding:            0,
      boxShadowSecondary: SHADOW.md,
    },
    Form: {
      labelFontSize:        FONT_SIZE.body,
      labelColor:           TEXT.secondary,
      labelHeight:          22,                    // off-scale
      verticalLabelPadding: "0 0 4px",
      itemMarginBottom:     SPACE.lg,
    },
    Checkbox: {
      controlInteractiveSize: SPACE.lg,
      borderRadiusSM:         RADIUS.xs,
    },
    Radio: {
      buttonBg:        SURFACE.surface,
      buttonCheckedBg: TONE.blue.softBg,
      buttonColor:     TEXT.primary,
    },
    Switch: {
      trackHeight:   22,                          // off-scale
      trackHeightSM: 16,
      handleSize:    18,
      handleSizeSM:  12,
    },
    Progress: {
      defaultColor:   TONE.blue.base,
      remainingColor: SURFACE.borderSoft,
    },
    Badge: {
      indicatorHeight:   18,                      // off-scale
      indicatorHeightSM: 14,
      fontSize:          FONT_SIZE.small,
    },
    Pagination: {
      itemSize:   CONTROL_HEIGHT.md,
      itemSizeSM: CONTROL_HEIGHT.sm,
    },
    Breadcrumb: {
      itemColor:      TEXT.faint,
      linkColor:      TEXT.secondary,
      linkHoverColor: TONE.blue.base,
      lastItemColor:  TEXT.primary,
      separatorColor: TEXT.faint,
      fontSize:       FONT_SIZE.small,
    },
    Layout: {
      // Editorial dark navy sidebar — matches BlueprintShell.
      siderBg:      SIDEBAR.bg,
      triggerBg:    SIDEBAR.surface,
      triggerColor: SIDEBAR.text,
      headerBg:     SURFACE.surface,
      bodyBg:       SURFACE.bg,
    },
    Menu: {
      // Dark Menu (sider) — editorial sidebar palette.
      darkItemBg:            SIDEBAR.bg,
      darkSubMenuItemBg:     SIDEBAR.bg,
      darkItemColor:         SIDEBAR.text,
      darkItemHoverBg:       SIDEBAR.hover,
      darkItemHoverColor:    TEXT.inverse,
      darkItemSelectedBg:    SIDEBAR.active,
      darkItemSelectedColor: TEXT.inverse,
      darkGroupTitleColor:   SIDEBAR.group,
    },
  },
};
